import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'

import { addNotification } from 'reducers/notification'
import * as ticketSelector from 'selectors/ticket'

import {
  selectAttachment,
  markAttachmentUploadAsFailed,
} from 'reducers/ticket'

import {
  useUploadAttachmentMutation,
  useUploadAttachmentAbortMutation,
} from 'api/tickets'

import { NOTIFICATION_TYPE_ERROR } from 'constants/notification'

import DropzoneFileInput from 'components/Shared/Form/DropzoneFileInput/Index'
import FileProgressbar from 'components/Shared/FileProgressbar/Index'

const ALLOWED_OCTET_STREAM_EXTENSIONS = [
  '.bin', '.dms', '.lha', '.lzh', '.class', '.ani', '.pgp', '.gpg', '.so', '.dll', '.dylib', '.bpk',
  '.deploy', '.dist', '.distz', '.dump', '.elc', '.lrf', '.mar', '.pkg', '.ipa', '.pcapng', '.chls',
]

const StyledForm = styled.div`
  display: flex;
  margin-bottom: 8px;
  flex-direction: column;
  max-width: 1112px;
`

const TicketAttachmentForm = ({ ticket }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const uploadSizeLimit = ticket.maxAttachmentSize
  const newAttachmentFileName = useSelector(ticketSelector.newAttachmentFileName)
  const isAttachmentUploadError = useSelector(ticketSelector.isAttachmentUploadError)
  const attachmentUploadFileName = useSelector(ticketSelector.attachmentUploadFileName)
  const attachmentUploadTotalBytes = useSelector(ticketSelector.attachmentUploadTotalBytes)
  const attachmentUploadCurrentBytes = useSelector(ticketSelector.attachmentUploadCurrentBytes)

  const [uploadAttachment] = useUploadAttachmentMutation()
  const [uploadAttachmentAbort] = useUploadAttachmentAbortMutation()

  const acceptedMimeTypes = useMemo(() => {
    // Browsers report a concrete type (.dll -> application/x-msdownload) or none at all
    // instead of application/octet-stream, so such files are matched by extension
    return ticket.availableMimeTypes?.reduce((a, v) => ({ ...a, [v]: v === 'application/octet-stream' ? ALLOWED_OCTET_STREAM_EXTENSIONS : [] }
    ), {}) || {}
  }, [ticket])

  const onSelectFile = (selectedFilesInfo) => {
    if (!selectedFilesInfo.length) {
      return
    }

    const selectedFile = selectedFilesInfo[0]
    dispatch(selectAttachment(selectedFile.name))

    const formData = new FormData()
    formData.append('attachments[]', selectedFile)

    uploadAttachment({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.externalId,
      fileInfo: formData,
      uploadAbortCallback: onUploadAbort,
    })
  }

  const onUploadAbort = () => {
    uploadAttachmentAbort()
  }

  const onFileSelectError = (files) => {
    const selectedFile = files[0]?.file

    dispatch(markAttachmentUploadAsFailed())

    if (!selectedFile) {
      return
    }

    const invalidFileByType = Boolean(!ticket.availableMimeTypes.includes(selectedFile.type))
    const invalidFileBySize = Boolean(selectedFile.size > uploadSizeLimit)

    // only single notification may be displayed
    if (invalidFileByType) {
      const translateParams = {
        extension: '.' + selectedFile.name.split('.').pop(),
        contentType: selectedFile.type || 'unknown type',
        interpolation: { escapeValue: false },
      }

      dispatch(addNotification({
        text: t('ticketPage.attachmentInvalidType', translateParams),
        type: NOTIFICATION_TYPE_ERROR,
        hideRefreshLink: true,
      }))
    } else if (invalidFileBySize) {
      dispatch(addNotification({
        text: t('ticketPage.attachmentTooBig'),
        type: NOTIFICATION_TYPE_ERROR,
        hideRefreshLink: true,
      }))
    }
  }

  return (
    <StyledForm>
      <DropzoneFileInput
        accept={acceptedMimeTypes}
        onChange={onSelectFile}
        title={newAttachmentFileName}
        onError={onFileSelectError}
        maxSize={uploadSizeLimit}
        isUploadFailed={isAttachmentUploadError}
      />
      <FileProgressbar
        fileName={attachmentUploadFileName}
        currentBytes={attachmentUploadCurrentBytes}
        totalBytes={attachmentUploadTotalBytes}
        onAbort={onUploadAbort}
      />
    </StyledForm>
  )
}

TicketAttachmentForm.propTypes = {
  ticket: PropTypes.object.isRequired,
}

export default TicketAttachmentForm
